import { useEffect, useState } from "react";
import api, { cachedGet } from "@/lib/api";
import { Loader2, Home, Car, HeartPulse, Plane, Smartphone, Scale, ShieldQuestion, ArrowUpRight, FileStack, Wallet, ShieldCheck, Copy, WifiOff, CreditCard } from "lucide-react";

const CAT_ICON = {
  "Logement": Home,
  "Véhicule": Car,
  "Santé": HeartPulse,
  "Mobilité & Voyage": Plane,
  "High-Tech": Smartphone,
  "Responsabilité Civile": Scale,
};

function scoreColor(s) {
  if (s >= 75) return "text-emerald-600";
  if (s >= 50) return "text-ochre";
  return "text-destructive";
}

export default function OverviewView({ onGoto }) {
  const [ov, setOv] = useState(null);
  const [report, setReport] = useState(null);
  const [redundancy, setRedundancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [a, b] = await Promise.all([cachedGet("/analysis/overview"), cachedGet("/analysis/report")]);
        setOv(a.data);
        setReport(b.data);
        setOffline(a.offline || b.offline);
      } catch {
        /* hors-ligne sans cache */
      } finally {
        setLoading(false);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("/analysis/card-redundancy");
        setRedundancy(data);
      } catch {
        setRedundancy(null);
      }
    })();
  }, []);

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-3 text-muted-foreground">
        <Loader2 className="w-7 h-7 animate-spin text-primary" />
        <p className="text-sm">Analyse de votre portefeuille…</p>
      </div>
    );

  const score = report?.protection_score ?? 0;
  const dupCount = (report?.items || []).filter((i) => i.type === "doublon").length;
  const optim = Math.max(0, Math.min(100, Math.round(score - Math.min(dupCount * 10, 40))));
  const optimLabel =
    optim >= 75 ? "Excellent — bien couvert, sans payer en double"
    : optim >= 50 ? "Correct — quelques optimisations possibles"
    : "À optimiser — doublons ou trous détectés";
  const actions = [...(report?.items || [])]
    .sort((a, b) => (b.type === "doublon" ? 1 : 0) - (a.type === "doublon" ? 1 : 0) || (b.savings || 0) - (a.savings || 0))
    .slice(0, 3);

  return (
    <div className="space-y-8 animate-in-up" data-testid="overview-view">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Tableau de bord</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Votre couverture, en un coup d'œil</h1>
        {offline && (
          <div data-testid="overview-offline" className="mt-3 inline-flex items-center gap-2 text-xs font-medium text-ochre bg-ochre/10 border border-ochre/25 rounded-full px-3 py-1.5">
            <WifiOff className="w-3.5 h-3.5" /> Hors-ligne — données de votre dernière visite
          </div>
        )}
      </div>

      {/* Score d'optimisation global */}
      <div data-testid="optimization-card" className="rounded-2xl border border-border bg-card p-6 sm:p-7">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><ShieldCheck className="w-6 h-6" /></div>
            <div>
              <p className="font-heading font-bold text-lg leading-tight">Êtes-vous bien couvert sans payer en double ?</p>
              <p className="text-sm text-muted-foreground mt-0.5">{optimLabel}</p>
            </div>
          </div>
          <div className="flex items-end gap-1.5">
            <span data-testid="optimization-score" className={`font-heading text-5xl font-extrabold tracking-tight ${scoreColor(optim)}`}>{optim}</span>
            <span className="text-xl text-muted-foreground font-heading font-bold mb-1">/100</span>
          </div>
        </div>
        <div className="mt-4 h-2.5 rounded-full bg-muted overflow-hidden">
          <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={optim} className={`h-full rounded-full transition-all duration-700 ${optim >= 75 ? "bg-emerald-500" : optim >= 50 ? "bg-ochre" : "bg-destructive"}`} style={{ width: `${optim}%` }} />
        </div>
        {actions.length > 0 ? (
          <div className="mt-4 space-y-2" data-testid="optim-advice">
            <p className="text-xs font-mono uppercase tracking-wide text-muted-foreground">Pour faire monter votre score</p>
            {actions.map((a, i) => (
              <button key={i} data-testid={`optim-action-${i}`} onClick={() => onGoto("report")} className="w-full text-left flex items-start gap-2.5 rounded-xl border border-border p-3 hover:bg-muted/50 transition">
                <span className={`w-6 h-6 rounded-md grid place-items-center shrink-0 mt-0.5 ${a.type === "doublon" ? "bg-ochre/15 text-ochre" : "bg-primary/10 text-primary"}`}>
                  {a.type === "doublon" ? <Copy className="w-3.5 h-3.5" /> : <ShieldQuestion className="w-3.5 h-3.5" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="text-sm font-medium">{a.type === "doublon" ? "Résilier / regrouper" : "Combler"} : {a.title}</span>
                  {a.type === "doublon" && a.savings > 0 && <span className="text-xs text-emerald-600 font-semibold ml-1.5">≈ {Math.round(a.savings)} €/an</span>}
                  <span className="block text-xs text-muted-foreground mt-0.5 line-clamp-1">{a.detail}</span>
                </span>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
              </button>
            ))}
          </div>
        ) : (
          <div className="mt-4 flex items-center gap-2 text-sm text-emerald-600" data-testid="optim-advice">
            <ShieldCheck className="w-4 h-4" /> Aucune action nécessaire — votre couverture est bien optimisée.
          </div>
        )}
      </div>

      {redundancy?.total_annual_savings > 0 && (
        <button
          data-testid="card-redundancy-cta"
          onClick={() => onGoto("report")}
          className="w-full text-left rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-5 sm:p-6 hover:bg-emerald-500/10 transition flex items-center gap-4"
        >
          <div className="w-11 h-11 rounded-xl bg-emerald-500/15 text-emerald-600 grid place-items-center shrink-0">
            <CreditCard className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold">Vos cartes couvrent déjà une assurance payante</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              Jusqu'à <span className="font-semibold text-emerald-600">{Math.round(redundancy.total_annual_savings)} €/an</span> économisables — voir le détail dans Doublons &amp; Failles.
            </p>
          </div>
          <ArrowUpRight className="w-5 h-5 text-emerald-600 shrink-0" />
        </button>
      )}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        {/* Score card */}
        <div className="md:col-span-5 rounded-2xl border border-border bg-card p-6 sm:p-8 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-muted-foreground">Niveau de protection global</p>
            <ShieldQuestion className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="mt-6 flex items-end gap-2">
            <span data-testid="protection-score" className={`font-heading text-6xl font-extrabold tracking-tight ${scoreColor(score)}`}>{score}</span>
            <span className="text-2xl text-muted-foreground font-heading font-bold mb-1.5">/100</span>
          </div>
          <div className="mt-4 h-2.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full bg-primary transition-all duration-700" style={{ width: `${score}%` }} />
          </div>
        </div>

        {/* Stats */}
        <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-5">
          <Stat icon={FileStack} label="Contrats analysés" value={ov?.total_contracts ?? 0} />
          <Stat icon={Wallet} label="Coût mensuel" value={`${Math.round(ov?.total_monthly_cost ?? 0)} €`} />
          <Stat icon={Wallet} label="Coût annuel" value={`${Math.round(ov?.total_annual_cost ?? 0)} €`} />
          <div className="sm:col-span-3 rounded-2xl border border-ochre/30 bg-accent text-accent-foreground p-6 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-80">Économies détectées</p>
              <p className="font-heading text-3xl font-extrabold mt-1">{Math.round(report?.annual_savings ?? 0)} € / an</p>
            </div>
            <button data-testid="goto-report-btn" onClick={() => onGoto("report")} className="flex items-center gap-1.5 text-sm font-medium hover:underline">
              Voir le détail <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Categories bento */}
      <div>
        <h2 className="font-heading text-xl font-bold tracking-tight mb-4">Par catégorie</h2>
        {(!ov || ov.categories.length === 0) ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
            <p className="text-sm">Aucun contrat pour l'instant.</p>
            <button onClick={() => onGoto("contracts")} className="mt-3 text-sm font-medium text-primary hover:underline">Ajouter un contrat →</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ov.categories.map((c) => {
              const Icon = CAT_ICON[c.category] || ShieldQuestion;
              return (
                <div key={c.category} data-testid={`cat-${c.category}`} className="rounded-2xl border border-border bg-card p-5 hover:border-primary/40 hover:shadow-sm transition group">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary grid place-items-center group-hover:scale-105 transition">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground">{Math.round(c.monthly_cost)} €/mois</span>
                  </div>
                  <p className="font-heading font-bold mt-3">{c.category}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{c.count} contrat{c.count > 1 ? "s" : ""}</p>
                  <div className="mt-3 space-y-1.5">
                    {c.contracts.map((ct, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="truncate pr-2">{ct.name}</span>
                        <span className="text-xs text-muted-foreground shrink-0">{ct.coverages} gar.</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 flex flex-col justify-between">
      <Icon className="w-5 h-5 text-muted-foreground" />
      <div className="mt-6">
        <p className="font-heading text-2xl font-extrabold tracking-tight">{value}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      </div>
    </div>
  );
}
